import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { DynamoConnector } from '../../DynamoConnector';
import { SORT_MAP } from './leaderboardSlice';
import { setAppRoute } from './appSlice';
import { defaultDetailListQuery, isSwitchingEntity, resetDetailListQuery } from './detailListQuery';
import { nextPageCursor } from './listPaging';

export const authorDetailsSlice = createSlice({
    name: 'authorDetails',
    initialState: {
        listItems: [],
        nextCursor: null,
        isFetching: false,
        loadingState: `idle`,
        details: null,
        ...defaultDetailListQuery(),
        isRefreshing: false,
    },
    reducers: {
        setIsRefreshing: (state, action) => {
            state.isRefreshing = action.payload;
        },
        setAuthorDetailsItems: (state, action) => {
            state.listItems = action.payload.map((item) => {
                return {
                    ...item,
                    key: item?.id,
                    storeKey: 'authorDetails',
                };
            });
        },
        addNewAuthorDetailsItems: (state, action) => {
            const { cursor, isReload } = action.payload;

            state.nextCursor = cursor || null;
            const offset = state.listItems.length;
            const newItems = action.payload.items.map((item, i) => {
                return {
                    ...item,
                    key: item?.id,
                    storeKey: 'authorDetails',
                    indexPrefix: 'authorDetails_',
                    index: offset + i,
                };
            });

            if (!isReload) {
                state.listItems = state.listItems.concat(newItems);
            } else {
                state.listItems = newItems;
            }

            state.isFetching = false;
            state.loadingState = `idle`;
        },
        setAuthorDetailsIsFetching: (state, action) => {
            state.isFetching = action.payload.isFetching;
            state.loadingState = action.payload.loadingState;
        },
        setAuthorDetailsFilters: (state, action) => {
            state.filters = action.payload;
        },
        setAuthorDetailsSortBy: (state, action) => {
            state.sortBy = action?.payload || SORT_MAP.POWERLEVEL;
        },
        setAuthorDetailsSortDirection: (state, action) => {
            state.sortDirection = action?.payload || SORT_MAP.DIRECTION.DESC;
        },
        setAuthorDetails: (state, action) => {
            state.details = action.payload;
        },
        resetAuthorDetailsListQuery: resetDetailListQuery,
        setSelectedListIndex: (state, action) => {
            state.selectedListIndex = action?.payload;
        },
    },
});

export const {
    setIsRefreshing,
    setAuthorDetailsItems,
    setAuthorDetails,
    addNewAuthorDetailsItems,
    setAuthorDetailsIsFetching,
    setAuthorDetailsFilters,
    setAuthorDetailsSortBy,
    setAuthorDetailsSortDirection,
    resetAuthorDetailsListQuery,
    setSelectedListIndex,
} = authorDetailsSlice.actions;

export const fetchAuthorDetails = (id) => (dispatch, getState) => {
    if (isSwitchingEntity([getState().authorDetails.details?.id], id)) {
        dispatch(resetAuthorDetailsListQuery());
    }
    dispatch(setAuthorDetails(null));
    const { filters, sortBy, sortDirection } = getState().authorDetails;
    dispatch(fetchAuthorDetailsItems(null, filters, true, sortBy, sortDirection, id));

    DynamoConnector.getAuthorDetails(id, (results) => {
        if (results?.error && (results.status === 400 || results.status === 404)) {
            const msg =
                results.status === 400
                    ? `That doesn't look like a valid author id.`
                    : `We don't have data for that author.`;
            toast(msg, { type: 'warning' });
            dispatch(setAppRoute({ route: '/users' }));
            return;
        }
        dispatch(setAuthorDetails(results || null));
    });
};

export const fetchAuthorDetailsItems =
    (cursor, filters = {}, isReload = false, sortBy, sortDirection, _id) =>
    (dispatch, getState) => {
        let id = !_id ? null : _id;

        if (!id) {
            if (window.location.pathname.includes(`/id/`)) {
                id = window.location.pathname.substring(window.location.pathname.indexOf(`/id/`) + 4);
            }
        }

        if (id) {
            filters = {
                ...filters,
                id,
            };
        }

        cursor = cursor !== -1 ? cursor : null;

        if (isReload) {
            dispatch(setAuthorDetailsItems([]));
        }

        dispatch(
            setAuthorDetailsIsFetching({
                isFetching: true,
                loadingState: cursor ? `loadingMore` : `loading`,
            })
        );

        DynamoConnector.getList(cursor, 'authors', sortBy?.index || null, filters, sortDirection, (results) => {
            dispatch(
                addNewAuthorDetailsItems({
                    items: results?.Items || [],
                    cursor: nextPageCursor(results, getState().authorDetails.listItems.length),
                    isReload,
                })
            );
        });
    };

export default authorDetailsSlice.reducer;
